import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import AppRouter from "./AppRouter.js";
import { useVersionChecker } from "./utils/versionChecker";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector, useDispatch } from "react-redux";
import { themeColors } from "./styles/theme";
import 'bootstrap/dist/css/bootstrap.min.css';
import "./App.css";
import "./styles/ag-grid-custom-styles.css";
import Toaster, { showToast } from "./Toaster";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import InfoModal from "./components/InfoModal/InfoModal.jsx";
import NotificationPermissionModal from "./components/NotificationPermissionModal/NotificationPermissionModal";
import { setNotificationPreference, registerPushSubscription } from "./apiServices/reducer/apiService";
import {
  isPushSupported,
  requestNotificationPermission,
  preparePushSubscriptionPayload,
  registerPushServiceWorker,
  showNotificationIfAllowed,
} from "./utils/pushNotification";
import {
  markNotificationPromptPending,
  markNotificationPromptSettled,
  resetNotificationPromptGate,
} from "./utils/notificationPromptGate";
//import { getSpaHomePath } from "./utils/spaHome";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

// Routes that must always be processed by the Login component using the
// real browser URL (e.g. clicked from emails). For these we ignore the
// previously-saved in-app path so token validation can run.
const SPECIAL_BROWSER_PATHS = [
  "/secure-login",
  "/reset_password",
  "/access-code-validate",
  "/signup", 
];

// Browser paths that are valid entry points for the SPA.
// Any other deep link (e.g. `/215646`) should land on Login (`/`).
const VALID_BROWSER_PATH_PREFIXES = [
  "/signup",
  "/user-landing-page",
  "/reports",
  "/blog",
  "/provider-landing-page",
  "/admin-landing-page",
  "/access-landing-page",
  "/landing-page",
  "/patient-summary",
  "/reset_password",
  "/secure-login",
  "/access-code-validate",
  "/404",
];

const normalizeBrowserPath = (rawPathname) => {
  const pathname = rawPathname || "/";

  // CRA can be hosted under a sub-path (e.g. `/pmr`). When that happens, the
  // browser URL includes the base prefix but our in-app routes do not.
  // Prefer PUBLIC_URL if provided; otherwise fall back to known base prefixes.
  let basePrefix = "";
  try {
    const configuredPublicUrl = (process.env.PUBLIC_URL || "").trim();
    const publicUrl = configuredPublicUrl
      ? new URL(configuredPublicUrl, window.location.origin).pathname.replace(/\/$/, "")
      : "";
    if (
      publicUrl &&
      publicUrl !== "/" &&
      (pathname === publicUrl || pathname.startsWith(publicUrl + "/"))
    ) {
      basePrefix = publicUrl;
    }
  } catch (e) {
    // ignore
  }

  if (!basePrefix) {
    if (pathname === "/pmr" || pathname.startsWith("/pmr/")) {
      basePrefix = "/pmr";
    } else if (pathname === "/pmr_qa" || pathname.startsWith("/pmr_qa/")) {
      basePrefix = "/pmr_qa";
    }
  }

  const appPath =
    basePrefix && (pathname === basePrefix || pathname.startsWith(basePrefix + "/"))
      ? (pathname.slice(basePrefix.length) || "/")
      : pathname;

  return { basePrefix, appPath };
};

const isValidBrowserAppPath = (appPath) =>
  appPath === "/" ||
  VALID_BROWSER_PATH_PREFIXES.some(
    (p) => appPath === p || appPath.startsWith(p + "/")
  );

// Login stores the session before navigation. The notification prompt must
// stay hidden on these routes so it does not cover the login loading screen.
const LOGIN_APP_PATHS = [
  "/signup",
  "/reset_password",
  "/secure-login",
  "/access-code-validate",
];

const isLoginAppPath = (appPath) => {
  const path = (appPath || "/").toLowerCase();
  if (path === "/" || path === "") return true;
  return LOGIN_APP_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
};

function NotificationRouteSync({ onPathChange }) {
  const location = useLocation();

  useEffect(() => {
    onPathChange(location.pathname || "/");
  }, [location.pathname, onPathChange]);

  return null;
}

/** CRA `PUBLIC_URL` / sub-path hosting: React Router must use the same basename. */
const getRouterBasename = () => {
  try {
    const configured = (process.env.PUBLIC_URL || "").trim();
    if (!configured) return undefined;
    const pathname = new URL(configured, window.location.origin).pathname.replace(/\/$/, "");
    return pathname && pathname !== "/" ? pathname : undefined;
  } catch (e) {
    return undefined;
  }
};

function App() {
  // If the app is reachable via multiple hosts, keep users on the canonical host.
  // Note: this only runs if the SPA actually loads (DNS/TLS must succeed).
  useEffect(() => {
    try {
      const canonicalHost =
        (process.env.REACT_APP_CANONICAL_HOST || "").trim() || "acmehealth.ai";
      const host = window.location.hostname;
      const isDev =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "[::1]";

      if (!isDev && host && host !== canonicalHost) {
        const target = `${window.location.protocol}//${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(target);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [darkMode, setDarkMode] = useState(prefersDarkMode);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.apiService?.login);
  const token = useSelector((state) => state.apiService?.token);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isSubmittingNotificationPref, setIsSubmittingNotificationPref] = useState(false);
  const [notificationModalFeedback, setNotificationModalFeedback] = useState(null);
  const [appPathname, setAppPathname] = useState(() => {
    try {
      return normalizeBrowserPath(window.location.pathname).appPath || "/";
    } catch (e) {
      return "/";
    }
  });
  const modalCloseTimerRef = useRef(null);
  const handleAppPathChange = useCallback((pathname) => {
    const next = pathname || "/";
    setAppPathname((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    window.__PMR_SPA_READY__ = true;
  }, []);

  useVersionChecker();

  const authedRef = useRef(false);
  authedRef.current = Boolean(
    userData?.user_id &&
    ((typeof token === 'string' && token.trim()) || (userData?.Token != null && String(userData.Token).trim()))
  );

  useEffect(() => {
    const stayInAppOnBack = (event) => {
      if (!authedRef.current) return;
      let path = '/';
      try {
        path = normalizeBrowserPath(window.location.pathname).appPath || '/';
      } catch (e) {
        path = window.location.pathname || '/';
      }
      if (path !== '/' && path !== '') return;

      let next = '/landing-page';
      try {
        const last = sessionStorage.getItem('pmr_last_app_path') || '';
        const allowed = ['/landing-page', '/admin-landing-page', '/provider-landing-page', '/user-landing-page', '/access-landing-page', '/reports', '/blog', '/patient-summary'];
        if (allowed.some((prefix) => last === prefix || last.startsWith(`${prefix}?`) || last.startsWith(`${prefix}/`))) {
          next = last;
        }
      } catch (e) {
        // ignore
      }

      event.stopImmediatePropagation();
      window.history.pushState(window.history.state, '', next);
    };

    window.addEventListener('popstate', stayInAppOnBack, true);
    return () => window.removeEventListener('popstate', stayInAppOnBack, true);
  }, []);

  useEffect(() => {
    if (!userData?.user_id || !isPushSupported()) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    registerPushServiceWorker().catch(() => {});
  }, [userData?.user_id]);

  // Run before BrowserRouter mounts so the first history location is normalized (invalid deep links → `/`).
  useMemo(() => {
    try {
      const { basePrefix, appPath } = normalizeBrowserPath(window.location.pathname);
      if (SPECIAL_BROWSER_PATHS.some((p) => appPath.startsWith(p))) {
        return null;
      }
      if (!isValidBrowserAppPath(appPath)) {
        try {
          window.history.replaceState(null, "", `${basePrefix || ""}/`);
        } catch (e) {
          // ignore
        }
        try {
          sessionStorage.removeItem("currentPath");
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore
    }
    return null;
  }, []);

  const muiTheme = useMemo(() =>
    createTheme({
      palette: {
        mode: darkMode ? "dark" : "light",
        primary: { main: themeColors.btnPrimary },
        secondary: { main: themeColors.primary },
        error: { main: themeColors.danger },
        success: { main: themeColors.success },
        background: { default: themeColors.bg, paper: themeColors.white },
        text: { primary: themeColors.textDark, secondary: themeColors.textMuted },
      },
    })
  );

  const handleSessionExpired = useCallback(() => {
    setShowSessionExpired(true);
  }, []);

  useEffect(() => {
    // If the interceptor fired before React mounted, this flag ensures we still show the modal.
    try {
      if (window.__PMR_SESSION_EXPIRED__ === true) {
        setShowSessionExpired(true);
      }
    } catch (e) {
      // ignore
    }

    const onSessionExpired = () => handleSessionExpired();
    window.addEventListener("pmr:session-expired", onSessionExpired);
    return () => {
      window.removeEventListener("pmr:session-expired", onSessionExpired);
    };
  }, [handleSessionExpired]);

  const sessionExpiredInfoData = useMemo(
    () => ({
      infoHeader: "Session Expired",
      infoMessage: "Your session is expired, please login again.",
      infoBtn1: "OK",
      infoBtnClass1: "primary",
      infoBtnValue1: "ok",
    }),
    []
  );

  const onSessionExpiredHide = useCallback(() => {
    setShowSessionExpired(false);
    try {
      window.__PMR_SESSION_EXPIRED__ = false;
    } catch (e) {
      // ignore
    }
    try {
    //  window.location.assign(getSpaHomePath());
    window.location.assign("/");
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    return () => {
      if (modalCloseTimerRef.current) {
        clearTimeout(modalCloseTimerRef.current);
      }
    };
  }, []);

  const scheduleModalClose = useCallback((delay = 3000) => {
    if (modalCloseTimerRef.current) {
      clearTimeout(modalCloseTimerRef.current);
    }
    modalCloseTimerRef.current = setTimeout(() => {
      setShowNotificationModal(false);
      setNotificationModalFeedback(null);
      modalCloseTimerRef.current = null;
      markNotificationPromptSettled();
    }, delay);
  }, []);

  useEffect(() => {
    if (notificationModalFeedback || isSubmittingNotificationPref) {
      return;
    }

    const isAuthenticated = Boolean(userData?.user_id && (token || userData?.Token));
    const preference = userData?.notification_preference;
    const isBrowserDenied =
      isPushSupported() &&
      typeof Notification !== "undefined" &&
      Notification.permission === "denied";

    if (isAuthenticated) {
      try {
        const dismissed = sessionStorage.getItem(`pmr_notif_modal_dismissed_${userData.user_id}`);
        if (dismissed === "true") {
          setShowNotificationModal(false);
          markNotificationPromptSettled();
          return;
        }
      } catch (e) {
        // ignore
      }

      if (isLoginAppPath(appPathname)) {
        setShowNotificationModal(false);
        return;
      }

      if (isBrowserDenied) {
        const isPrefAllow = Number(preference) === 1;
        const message = isPrefAllow
          ? "Your notification preference is enabled, but notifications are blocked in your browser. Please enable browser notifications to receive updates."
          : "Notifications are blocked in your browser. Please enable notifications in your browser settings to receive updates.";

        setNotificationModalFeedback({
          message,
          type: "warning",
        });
        setShowNotificationModal(true);
        markNotificationPromptPending();
      } else if (Number(preference) !== 1) {
        setNotificationModalFeedback(null);
        setShowNotificationModal(true);
        markNotificationPromptPending();
      } else {
        setShowNotificationModal(false);
        setNotificationModalFeedback(null);
        markNotificationPromptSettled();
      }
    } else {
      setShowNotificationModal(false);
      setNotificationModalFeedback(null);
      resetNotificationPromptGate();
    }
  }, [
    userData?.user_id,
    userData?.notification_preference,
    token,
    userData?.Token,
    notificationModalFeedback,
    isSubmittingNotificationPref,
    appPathname,
  ]);

  const handleNotificationModalClose = useCallback(() => {
    if (modalCloseTimerRef.current) {
      clearTimeout(modalCloseTimerRef.current);
      modalCloseTimerRef.current = null;
    }
    setShowNotificationModal(false);
    setNotificationModalFeedback(null);
    markNotificationPromptSettled();
    if (userData?.user_id) {
      try {
        sessionStorage.setItem(`pmr_notif_modal_dismissed_${userData.user_id}`, "true");
      } catch (e) {
        // ignore
      }
    }
  }, [userData?.user_id]);

  const handleNotificationModalBlock = useCallback(async () => {
    if (!userData?.user_id) return;
    setIsSubmittingNotificationPref(true);
    try {
      const resultAction = await dispatch(
        setNotificationPreference({
          user_id: userData.user_id,
          preference_type_id: 2,
          token: token || userData?.Token,
        })
      );

      if (setNotificationPreference.fulfilled.match(resultAction)) {
        if (userData?.user_id) {
          try {
            sessionStorage.setItem(`pmr_notif_modal_dismissed_${userData.user_id}`, "true");
          } catch (e) {
            // ignore
          }
        }
        setNotificationModalFeedback({
          message: "Notifications have been turned off.",
          type: "blocked",
        });
        scheduleModalClose(3000);
      } else {
        const errMsg =
          resultAction.payload?.message ||
          resultAction.payload ||
          resultAction.error?.message ||
          "Failed to update notification preference.";
        showToast("error", typeof errMsg === "string" ? errMsg : "Failed to update notification preference.");
      }
    } catch (err) {
      showToast("error", err?.message || "Failed to update notification preference.");
    } finally {
      setIsSubmittingNotificationPref(false);
    }
  }, [dispatch, userData?.user_id, userData?.Token, token, scheduleModalClose]);

  const handleNotificationModalAllow = useCallback(async () => {
    if (!userData?.user_id) return;

    setIsSubmittingNotificationPref(true);
    try {
      if (!isPushSupported()) {
        showToast("error", "Web Push notifications are not supported in this browser.");
        return;
      }

      const permission = await requestNotificationPermission();

      if (permission === "denied") {
        if (userData?.user_id) {
          try {
            sessionStorage.setItem(`pmr_notif_modal_dismissed_${userData.user_id}`, "true");
          } catch (e) {
            // ignore
          }
        }
        setNotificationModalFeedback({
          message:
            "Notifications are blocked in your browser. Please enable notifications in your browser settings to receive updates.",
          type: "warning",
        });
        scheduleModalClose(5000);
        return;
      }

      if (permission !== "granted") {
        showToast("info", "Notification permission was not granted.");
        return;
      }

      const payload = await preparePushSubscriptionPayload(userData.user_id);
      const resultAction = await dispatch(
        registerPushSubscription({
          ...payload,
          token: token || userData?.Token,
        })
      );

      if (registerPushSubscription.fulfilled.match(resultAction)) {
        const prefAction = await dispatch(
          setNotificationPreference({
            user_id: userData.user_id,
            preference_type_id: 1,
            token: token || userData?.Token,
          })
        );

        if (setNotificationPreference.fulfilled.match(prefAction)) {
          if (userData?.user_id) {
            try {
              sessionStorage.setItem(`pmr_notif_modal_dismissed_${userData.user_id}`, "true");
            } catch (e) {
              // ignore
            }
          }
          setNotificationModalFeedback({
            message: "Notifications enabled successfully.",
            type: "success",
          });
          scheduleModalClose(3000);

          try {
            await showNotificationIfAllowed({
              userId: userData.user_id,
              token: token || userData?.Token,
              title: "Notifications enabled",
              body: "You will now receive important updates on this device.",
              url: window.location.href,
              tag: `pmr-enabled-${Date.now()}`,
            });
          } catch (notifyErr) {
            console.error("System notification failed:", notifyErr);
          }
        } else {
          const prefErrMsg =
            prefAction.payload?.message ||
            prefAction.payload ||
            prefAction.error?.message ||
            "Failed to save notification preference.";
          showToast(
            "error",
            typeof prefErrMsg === "string" ? prefErrMsg : "Failed to save notification preference."
          );
        }
      } else {
        const errMsg =
          resultAction.payload?.message ||
          resultAction.payload ||
          resultAction.error?.message ||
          "Failed to register push subscription on the server.";
        showToast("error", typeof errMsg === "string" ? errMsg : "Failed to register push subscription.");
      }
    } catch (err) {
      showToast("error", err?.message || "An unexpected error occurred during notification setup.");
    } finally {
      setIsSubmittingNotificationPref(false);
    }
  }, [dispatch, userData?.user_id, userData?.Token, token, scheduleModalClose]);

  return (

    <div className="App">
      <div>
        <BrowserRouter basename={getRouterBasename()}>
          <NotificationRouteSync onPathChange={handleAppPathChange} />
          <AppRouter />
        </BrowserRouter>
      </div>
      <Toaster />
      <InfoModal
        show={showSessionExpired}
        onHide={onSessionExpiredHide}
        infoData={sessionExpiredInfoData}
      />
      <NotificationPermissionModal
        show={showNotificationModal}
        onClose={handleNotificationModalClose}
        onBlock={handleNotificationModalBlock}
        onAllow={handleNotificationModalAllow}
        loading={isSubmittingNotificationPref}
        resultMessage={notificationModalFeedback?.message}
        resultType={notificationModalFeedback?.type}
      />
    </div>

  );
}

export default App;
